import { useMemo, type ReactNode } from "react";
import type { VideoCallingSlice, VideoCallingSession } from "../types";
import { VideoCallingHookContext } from "../hooks/useVideoCallingContext";

// ============================================
// Provider
// ============================================

/**
 * Props for the VideoCallingHookProvider.
 */
interface VideoCallingHookProviderProps {
  /** React children */
  children: ReactNode;
  /** The app's hook that selects videoCalling from their Zustand store */
  useVideoCalling: () => VideoCallingSlice;
  /** The app's session for API calls (Session or EnduserSession) */
  session: VideoCallingSession | null;
}

/**
 * Provider that injects the app's video calling hook and session.
 *
 * This allows shared components to access the video calling state without
 * knowing the app's specific Zustand store structure.
 *
 * @example
 * ```tsx
 * // In apps/web/src/providers/VideoCallingProvider.tsx
 * import { VideoCallingHookProvider, VideoCallWindow } from '@tellescope/video-calling';
 * import { useVideoCalling as useAppVideoCalling, useAuth } from '../store';
 *
 * export function VideoCallingProvider({ children }) {
 *   const { session } = useAuth();
 *
 *   return (
 *     <VideoCallingHookProvider
 *       useVideoCalling={useAppVideoCalling}
 *       session={session}
 *     >
 *       {children}
 *       <VideoCallWindow userRole="host" />
 *     </VideoCallingHookProvider>
 *   );
 * }
 * ```
 */
export function VideoCallingHookProvider({ children, useVideoCalling, session }: VideoCallingHookProviderProps) {
  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ useVideoCalling, session }), [useVideoCalling, session]);

  return <VideoCallingHookContext.Provider value={contextValue}>{children}</VideoCallingHookContext.Provider>;
}
